import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Command } from 'cmdk';
import { useNavigate } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@leanopx/design-system';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useUiStore } from '@/stores/ui-store';
import { useCommandPaletteStore } from '@/stores/command-palette-store';
import { useAuth, useIsOrgAdmin } from '@/components/providers/auth-provider';
import { IconGlyph, type IconGlyphName } from '@/components/ui/icon-glyph';
import { t, tFormat } from '@/lib/i18n';
import { cn } from '@/lib/utils';

/**
 * ⌘K command palette — ported from screens4.jsx `CommandPaletteScreen`.
 * Sectioned list with a top "Jump to" group (navigation routes), an
 * "Ask the agent" group (hand-off to the Ask surface), and a "Do" group
 * (verb dispatch for theme / density / org switching). The first row
 * carries the accent left-rail + surface-2 hover so the selected state
 * reads at-a-glance; the footer ribbon spells out the keyboard contract.
 *
 * Keyboard contract (per issue #14 acceptance criteria):
 *   - Esc       → close (handled by the cmdk dialog)
 *   - ↵ (Enter) → run the focused item's primary action
 *   - ⌘↵ / Ctrl↵→ run the focused item's *alternate* action when present
 *                 (e.g. Ask → "open in new tab" rather than "navigate")
 *
 * Alt-action dispatch lives in the `altHandlersRef` registry (DOM node →
 * callback). Both the keyboard handler and the Item's onClick route
 * through the same registry — that way:
 *   - ⌘↵ on the keyboard fires the alt action (no DOM `.click()`
 *     round-trip; programmatic clicks lose modifier flags so the
 *     prior implementation silently fell back to the primary action).
 *   - Cmd+Click on an item fires the alt action exactly once
 *     (`stopPropagation` prevents cmdk's internal click handler from
 *     also calling `onSelect` — without it both fired and Ask did a
 *     double navigation: `window.open` + `navigate`).
 *
 * The ROUTES export below is consumed by the sidebar + ⌘K kebab handler
 * — preserved verbatim so existing callers don't need to change.
 */

interface NavRoute {
  to: string;
  label: string;
  glyph: IconGlyphName;
  /** Mirror of sidebar-nav's `isOrgAdmin` gate — rows hidden from non-admins. */
  adminOnly?: boolean;
}

/** All routes the palette can jump to — kept in sync with sidebar-nav. */
const ROUTES: NavRoute[] = [
  { to: '/today', label: 'Briefing', glyph: 'today' },
  { to: '/ask', label: 'Ask the operator', glyph: 'ask' },
  { to: '/dossiers', label: 'Dossiers', glyph: 'dossiers' },
  { to: '/scorecard', label: 'Scorecard', glyph: 'scorecard' },
  { to: '/playbook', label: 'Playbook', glyph: 'playbook' },
  { to: '/rocks', label: 'Rocks', glyph: 'rocks' },
  { to: '/insights', label: 'Insights', glyph: 'insights' },
  { to: '/signals', label: 'Signals', glyph: 'signals' },
  { to: '/signals/external', label: 'Outside view', glyph: 'signals' },
  { to: '/alerts', label: 'Alerts', glyph: 'alerts' },
  { to: '/todos', label: 'Todos', glyph: 'todos' },
  { to: '/agent', label: 'Agent log', glyph: 'agent' },
  { to: '/sources', label: 'Sources', glyph: 'sources' },
  { to: '/bookmarks', label: 'Bookmarks', glyph: 'bookmarks' },
  // First-run guided tour. Reachable via ⌘K → "Walkthrough" or "Take a
  // tour" until the auto-launch-on-first-sign-in trigger lands.
  { to: '/walkthrough', label: 'Walkthrough (take a tour)', glyph: 'discovery' },
  { to: '/discovery', label: 'How we read you', glyph: 'discovery', adminOnly: true },
  { to: '/admin/people', label: 'People (admin)', glyph: 'watch', adminOnly: true },
  { to: '/admin/audit', label: 'Audit log', glyph: 'audit', adminOnly: true },
  { to: '/admin/settings', label: 'Settings', glyph: 'settings', adminOnly: true },
];

export { ROUTES };

export function CommandPalette() {
  const { open, close, setOpen } = useCommandPaletteStore();
  const navigate = useNavigate();
  const toggleTheme = useUiStore((s) => s.toggleTheme);
  const cycleDensity = useUiStore((s) => s.cycleDensity);
  const toggleSidePin = useUiStore((s) => s.toggleSidePin);
  const { memberships, activeOrg, switchOrg } = useAuth();
  const isOrgAdmin = useIsOrgAdmin();
  const [query, setQuery] = useState('');

  // Items register their alt-action handler here so the ⌘↵ keyboard
  // path can invoke it directly. Calling `Element.click()` would strip
  // the metaKey from the synthetic event, so the Item's onClick gate
  // never fires — we have to dispatch the handler ourselves.
  const altHandlersRef = useRef<Map<HTMLElement, () => void>>(new Map());

  // Reset the query each time the palette closes so the next open
  // starts fresh — otherwise stale text lingers between sessions.
  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  // Global ⌘K / Ctrl+K toggle. Esc-to-close is handled by the underlying
  // Radix dialog so we don't bind it here.
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(!open);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, setOpen]);

  function go(to: string) {
    navigate({ to });
    close();
  }

  function openAsk(q: string, alt: boolean) {
    // Default (↵): jump to /ask with the question staged via the `q`
    // search param. AskPage reads it on mount, pre-fills the input,
    // and strips the param so a refresh doesn't re-fire the question.
    // Alt (⌘↵): open the AI surface in a new tab so the user keeps
    // their current context — useful when the palette was opened
    // from a dossier or insight they want to keep on screen.
    const trimmed = q.trim();
    if (alt) {
      const encoded = trimmed ? encodeURIComponent(trimmed) : '';
      const path = `/ask${encoded ? `?q=${encoded}` : ''}`;
      window.open(path, '_blank', 'noopener,noreferrer');
      close();
    } else {
      navigate({
        to: '/ask',
        search: () => (trimmed ? { q: trimmed } : {}),
      });
      close();
    }
  }

  const visibleRoutes = useMemo(
    () => ROUTES.filter((r) => !r.adminOnly || isOrgAdmin),
    [isOrgAdmin],
  );
  const otherOrgs = memberships.filter((m) => m.id !== activeOrg?.id);
  const trimmedQuery = query.trim();
  // Static result total for the designer footer ribbon — visible nav
  // routes + Ask row (always present) + 3 "Do" rows + org-switch rows.
  // We surface a static count rather than the cmdk-filtered count so
  // the footer doesn't jitter on every keystroke (matches the design
  // reference at LeanOpx Platform.html §18 / CommandPaletteScreen).
  const resultTotal = visibleRoutes.length + 1 + 3 + otherOrgs.length;

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : close())}>
      <DialogContent
        hideClose
        className="overflow-hidden border-[var(--color-line-strong)] bg-[var(--color-surface)] p-0 shadow-[0_20px_60px_rgba(0,0,0,0.5)] sm:max-w-[720px]"
      >
        <VisuallyHidden>
          <DialogTitle>{t('overlay.commandPalette.a11y.title')}</DialogTitle>
          <DialogDescription>{t('overlay.commandPalette.a11y.description')}</DialogDescription>
        </VisuallyHidden>
        <Command
          label="Command palette"
          // Capture ⌘↵ at the cmdk root so the focused item's alt
          // action fires directly (no DOM round-trip — programmatic
          // `.click()` doesn't carry modifier flags so the prior
          // implementation silently degraded to the primary action).
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            if (!(e.metaKey || e.ctrlKey)) return;
            // The currently-highlighted Item has data-selected="true".
            // Look up its alt handler in the registry and invoke directly
            // — `Element.click()` would dispatch a synthetic click with
            // metaKey=false, so the Item's onClick gate would route to
            // the *primary* action instead of the alternate.
            const focused = (e.currentTarget as HTMLElement).querySelector<HTMLElement>(
              '[data-selected="true"][data-alt-action]',
            );
            const handler = focused ? altHandlersRef.current.get(focused) : undefined;
            if (handler) {
              e.preventDefault();
              handler();
            }
          }}
        >
          <div className="flex items-center gap-3 border-b border-[var(--color-line)] px-5 py-4">
            <Search className="size-[18px] text-[var(--color-ink-muted)]" aria-hidden />
            <Command.Input
              autoFocus
              value={query}
              onValueChange={setQuery}
              placeholder={t('overlay.commandPalette.placeholder')}
              className="flex-1 bg-transparent text-base text-[var(--color-ink)] outline-none placeholder:text-[var(--color-ink-muted)]"
            />
            <span className="rounded-xs border border-[var(--color-line)] bg-[var(--color-surface-2)] px-1.5 py-px font-mono text-2xs uppercase tracking-wider text-[var(--color-ink-muted)]">
              {t('overlay.commandPalette.escKey')}
            </span>
          </div>

          <Command.List className="max-h-[460px] overflow-y-auto">
            <Command.Empty className="p-6 text-center text-sm text-[var(--color-ink-muted)]">
              {t('overlay.commandPalette.empty')}
            </Command.Empty>

            <PaletteGroup heading={t('overlay.commandPalette.group.jumpTo')}>
              {visibleRoutes.map((r) => (
                <PaletteItem
                  key={r.to}
                  icon={<IconGlyph name={r.glyph} size={16} />}
                  hint={
                    r.adminOnly
                      ? t('overlay.commandPalette.hint.admin')
                      : t('overlay.commandPalette.hint.surface')
                  }
                  onSelect={() => go(r.to)}
                  altRegistry={altHandlersRef}
                >
                  {r.label}
                </PaletteItem>
              ))}
            </PaletteGroup>

            <PaletteGroup heading={t('overlay.commandPalette.group.ask')}>
              {trimmedQuery.length > 0 ? (
                <PaletteItem
                  icon={<IconGlyph name="ask" size={16} />}
                  hint={t('overlay.commandPalette.hint.synthesis')}
                  onSelect={() => openAsk(trimmedQuery, false)}
                  altLabel={t('overlay.commandPalette.altLabel.newTab')}
                  onAltSelect={() => openAsk(trimmedQuery, true)}
                  altRegistry={altHandlersRef}
                >
                  {t('overlay.commandPalette.ask.prefix')}{' '}
                  <span className="text-[var(--color-accent)]">{trimmedQuery}</span>
                </PaletteItem>
              ) : (
                <PaletteItem
                  icon={<IconGlyph name="ask" size={16} />}
                  hint={t('overlay.commandPalette.hint.synthesis')}
                  onSelect={() => openAsk('', false)}
                  altRegistry={altHandlersRef}
                >
                  {t('overlay.commandPalette.ask.open')}
                </PaletteItem>
              )}
            </PaletteGroup>

            <PaletteGroup heading={t('overlay.commandPalette.group.do')}>
              <PaletteItem
                icon={<IconGlyph name="settings" size={16} />}
                hint={t('overlay.commandPalette.hint.theme')}
                onSelect={() => {
                  toggleTheme();
                  close();
                }}
                altRegistry={altHandlersRef}
              >
                {t('overlay.commandPalette.do.toggleTheme')}
              </PaletteItem>
              <PaletteItem
                icon={<IconGlyph name="settings" size={16} />}
                hint={t('overlay.commandPalette.hint.density')}
                onSelect={() => {
                  cycleDensity();
                  close();
                }}
                altRegistry={altHandlersRef}
              >
                {t('overlay.commandPalette.do.cycleDensity')}
              </PaletteItem>
              <PaletteItem
                icon={<IconGlyph name="settings" size={16} />}
                hint={t('overlay.commandPalette.hint.sidebar')}
                onSelect={() => {
                  toggleSidePin();
                  close();
                }}
                altRegistry={altHandlersRef}
              >
                {t('overlay.commandPalette.do.toggleSidebar')}
              </PaletteItem>
              {otherOrgs.map((m) => (
                <PaletteItem
                  key={`switch-${m.id}`}
                  icon={<IconGlyph name="watch" size={16} />}
                  hint={t('overlay.commandPalette.hint.orgSwitch')}
                  onSelect={() => {
                    void switchOrg(m.id);
                    close();
                  }}
                  altRegistry={altHandlersRef}
                >
                  {tFormat('overlay.commandPalette.do.switchOrg', { name: m.name })}
                </PaletteItem>
              ))}
            </PaletteGroup>
          </Command.List>

          <div className="flex items-center gap-5 border-t border-[var(--color-line)] px-5 py-2.5 font-mono text-xs tracking-[0.04em] text-[var(--color-ink-muted)]">
            <span className="inline-flex items-center gap-1.5">
              <Kbd>↑↓</Kbd> {t('overlay.commandPalette.foot.nav')}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Kbd>↵</Kbd> {t('overlay.commandPalette.foot.open')}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Kbd>⌘↵</Kbd> {t('overlay.commandPalette.foot.newPane')}
            </span>
            <span className="ms-auto" aria-live="polite">
              {tFormat('overlay.commandPalette.foot.results', { n: resultTotal })}
            </span>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

function Kbd({ children }: { children: ReactNode }) {
  return (
    <span className="rounded-xs border border-[var(--color-line)] bg-[var(--color-surface-2)] px-1.5 py-px text-2xs text-[var(--color-ink-muted)]">
      {children}
    </span>
  );
}

function PaletteGroup({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <Command.Group
      heading={heading}
      className={cn(
        // cmdk renders its heading inside a `[cmdk-group-heading]` slot —
        // restyle it to the designer's mono eyebrow rather than the
        // library default. Items get a slim vertical rhythm via px-5.
        'px-5 [&_[cmdk-group-heading]]:px-0 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-3 [&_[cmdk-group-heading]]:font-mono [&_[cmdk-group-heading]]:text-2xs [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.14em] [&_[cmdk-group-heading]]:text-[var(--color-ink-muted)]',
      )}
    >
      {children}
    </Command.Group>
  );
}

function PaletteItem({
  children,
  icon,
  hint,
  onSelect,
  altLabel,
  onAltSelect,
  altRegistry,
}: {
  children?: ReactNode;
  icon?: ReactNode;
  /** Mono hint on the right side ("Admin", "Synthesis · cited" etc.). */
  hint?: string;
  onSelect: () => void;
  /** Label for the alternate (⌘↵) action; only rendered when set. */
  altLabel?: string;
  /** Triggered when the user hits ⌘↵ on this item or Cmd-clicks it. */
  onAltSelect?: () => void;
  /**
   * Registry the palette uses to look up alt handlers from the keyboard
   * path — invoked directly because `Element.click()` strips metaKey.
   */
  altRegistry?: React.RefObject<Map<HTMLElement, () => void>>;
}) {
  // Register the alt handler against the rendered DOM node so the
  // root onKeyDown can call it without round-tripping through a
  // synthetic click (which would lose the metaKey/ctrlKey flags).
  // React 19 ref-callback cleanup pattern: the returned function
  // runs when the node unmounts, so each entry self-removes.
  const registerRef = (node: HTMLDivElement | null) => {
    const map = altRegistry?.current;
    if (!map || !node || !onAltSelect) return undefined;
    map.set(node, onAltSelect);
    return () => {
      map.delete(node);
    };
  };
  return (
    <Command.Item
      ref={registerRef}
      onSelect={onSelect}
      // The cmdk "click" path bypasses the keyboard handler at the root,
      // so detect ⌘+click here and dispatch the alt action exactly
      // once. `stopPropagation` prevents cmdk's internal click handler
      // from also firing `onSelect` (the prior `preventDefault`-only
      // version caused Ask to do a double navigation: window.open +
      // navigate).
      onClick={(e) => {
        if ((e.metaKey || e.ctrlKey) && onAltSelect) {
          e.preventDefault();
          e.stopPropagation();
          onAltSelect();
        }
      }}
      className={cn(
        'group flex cursor-pointer items-center gap-3.5 py-2.5 text-base text-[var(--color-ink)]',
        // The designer's accent left-rail + surface-2 fill on the
        // selected (highlighted) row — cmdk drives this via the
        // [data-selected="true"] attribute on the focused item.
        'border-l-2 border-transparent data-[selected=true]:border-[var(--color-accent)] data-[selected=true]:bg-[var(--color-surface-2)]',
      )}
    >
      {icon && <span className="text-[var(--color-ink-muted)]">{icon}</span>}
      <span className="flex-1 truncate">{children}</span>
      {hint && (
        <span className="hidden font-mono text-xs text-[var(--color-ink-muted)] sm:inline">
          {hint}
        </span>
      )}
      {altLabel ? (
        <span className="hidden rounded-xs border border-[var(--color-line)] bg-[var(--color-surface-2)] px-1.5 py-px font-mono text-2xs uppercase tracking-wider text-[var(--color-ink-muted)] group-data-[selected=true]:inline">
          ⌘↵ {altLabel}
        </span>
      ) : (
        <span
          className="hidden rounded-xs border border-[var(--color-line)] bg-[var(--color-surface-2)] px-1.5 py-px font-mono text-2xs uppercase tracking-wider text-[var(--color-ink-muted)] group-data-[selected=true]:inline"
          aria-hidden
        >
          ↵
        </span>
      )}
    </Command.Item>
  );
}
